import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import type { ApiResponse, ContactDetailsDto, UpdateContactDetailsBody } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { contactDetailsQueryKey, EMPTY_DETAILS } from '@/hooks/queries/use-contact-details-query';
import { contactSummaryQueryKey } from '@/hooks/queries/use-contact-summary-query';

interface UpdateContactDetailsContext {
  previous?: ContactDetailsDto;
}

const updateContactDetails = async (
  creatorIdentityId: string,
  patch: UpdateContactDetailsBody
): Promise<ContactDetailsDto> => {
  const { data } = await api.patch<ApiResponse<ContactDetailsDto>>(
    `/dashboard/contacts/${creatorIdentityId}/details`,
    patch
  );

  return data.data ?? EMPTY_DETAILS;
};

/**
 * Upserts the brand's contact override (email/phone), optimistically merging the
 * patch into the cached row. Mirrors useUpdateContactNote's cache conventions
 * (cancel + snapshot + setQueryData, rollback on error, invalidate on settled).
 * No success toast — the field commits silently.
 */
export const useUpdateContactDetails = (
  creatorIdentityId: string | null,
  brandId: string | null | undefined = null
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = contactDetailsQueryKey(creatorIdentityId);

  return useMutation<
    ContactDetailsDto,
    AxiosError<ApiResponse>,
    UpdateContactDetailsBody,
    UpdateContactDetailsContext
  >({
    mutationFn: (patch) =>
      trackProductAction('contacts', 'update', () =>
        updateContactDetails(creatorIdentityId!, patch)
      ),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<ContactDetailsDto>(queryKey);

      queryClient.setQueryData<ContactDetailsDto>(queryKey, (current) => ({
        ...(current ?? EMPTY_DETAILS),
        ...patch,
      }));

      return { previous };
    },
    onError: (error, _patch, context) => {
      queryClient.setQueryData(queryKey, context?.previous);

      toaster.error({
        title: t('contactDrawer.rail.contact.updateError'),
        description: error.response?.data?.message || t('common.errors.unexpected'),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      // The summary's `hasEmail` (which gates the header's email CTA) is derived
      // from this override, so refresh it here too. Otherwise the CTA stays
      // disabled until a full reload after the brand first adds an email.
      queryClient.invalidateQueries({
        queryKey: contactSummaryQueryKey(brandId, creatorIdentityId),
      });
      // The resolve query returns the server-collapsed `primaryEmail` /
      // `emailOrigin` that the contact rail reads as its single source of truth.
      // Without this refresh, clearing an override would redisplay the stale
      // resolved email (its 5-min staleTime survives the close/reopen). This
      // hook only knows the identity id, not the resolve ref (entryType/id/
      // platform), so invalidate the whole `contact-resolve` prefix — the
      // narrowest key available here.
      queryClient.invalidateQueries({ queryKey: ['contact-resolve'] });
      // The wishlist rows also surface the resolved email + provenance, so keep
      // that list in sync after the brand edits the override.
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
    },
  });
};
